import React, { useState, useMemo } from 'react';
import DataTable from 'react-data-table-component';
import { MdArrowUpward, MdSearch, MdArrowDropDown, MdError, MdWarning, MdInfo, MdBugReport, MdRefresh, MdFileDownload, MdExpandMore, MdExpandLess, MdClose } from 'react-icons/md';
import trash from '../../Assets/trash.svg';
import LogLevelDropdown from './LogLevelDrodown';
import edit from '../../Assets/edit.svg';
import Modal from '../Modals/Modal';
import Modals from '../Modals/Modals';
import close from '../../Assets/closeP.svg';
import '../styles.css';
import axios from 'axios';
import { CSVLink } from 'react-csv';

const customSortIcon = <MdArrowUpward />;

const sortText = (rowA, rowB, selector, direction) => {
  const a = (selector(rowA) || '').toString().toLowerCase();
  const b = (selector(rowB) || '').toString().toLowerCase();

  if (a < b) return direction === 'asc' ? -1 : 1;
  if (a > b) return direction === 'asc' ? 1 : -1;
  return 0;
};

const TableDevice = ({ rows, headings, onDelete, onEditDevice, userData, onStatusCheck, onSettingsClick }) => {
  // State management
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [currentErrorDescription, setCurrentErrorDescription] = useState('');
  const [codeError, setCodeCurrentError] = useState('');
  const [isModalOpenLog, setIsModalOpenLog] = useState(false);
  const [logData, setLogData] = useState('');
  const [selectedLogLevel, setSelectedLogLevel] = useState('INFO');
  const [filteredLogData, setFilteredLogData] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [logMacAddress, setLogMacAddress] = useState('');
  const [selectedLogLevels, setSelectedLogLevels] = useState({});
  const storedRole = userData?.role;

  // Log level configuration
  const logLevels = [
    { value: 'ERROR', label: 'ERROR', priority: 4, color: '#F04438', icon: <MdError /> },
    { value: 'WARN', label: 'WARN', priority: 3, color: '#F79009', icon: <MdWarning /> },
    { value: 'INFO', label: 'INFO', priority: 2, color: '#12B76A', icon: <MdInfo /> },
  ];

  // Parse log data into structured format
  const parseLogData = (logText) => {
    if (!logText) return [];
    // First, parse each line as before
    const parsedLines = logText
      .split('\n')
      .filter(line => line.trim())
      .map(line => {
        let timestamp = '';
        let level = 'INFO';
        let event = line;

        const timestampMatch = line.match(/^([A-Za-z]{3},\s\d{1,2}\s[A-Za-z]{3}\s\d{4}\s\d{1,2}:\d{2}:\d{2})/);
        if (timestampMatch) {
          timestamp = timestampMatch[1];
          event = line.replace(timestamp, '').trim();
        }

        if (event.toLowerCase().includes('error') || event.toLowerCase().includes('failed')) {
          level = 'ERROR';
        } else if (event.toLowerCase().includes('warn') || event.toLowerCase().includes('warning')) {
          level = 'WARN';
        } else if (event.toLowerCase().includes('info')) {
          level = 'INFO';
        } else if (event.toLowerCase().includes('debug')) {
          level = 'DEBUG';
        }

        if (!timestamp) {
          const now = new Date();
          timestamp = now.toLocaleString('en-US', { timeZone: 'UTC' });
        }

        return { timestamp, level, event };
      })
      .filter(row => row.timestamp || row.level || (row.event && row.event.trim() !== ''));

    // Group by timestamp
    const grouped = {};
    parsedLines.forEach(line => {
      if (!grouped[line.timestamp]) {
        grouped[line.timestamp] = { 
          timestamp: line.timestamp, 
          level: line.level, 
          events: [line.event] 
        };
      } else {
        grouped[line.timestamp].events.push(line.event);
      }
    });

    // Convert grouped object to array
    return Object.values(grouped).map(item => ({
      timestamp: item.timestamp,
      level: item.level,
      event: item.events.join('\n') // Combine all events for this timestamp
    }));
  };

  const sortedLogData = useMemo(() => {
    const parsed = parseLogData(filteredLogData);
    // Sort by timestamp descending (latest first)
    return parsed.sort((a, b) => {
      // Try to parse as date, fallback to string compare
      const dateA = new Date(a.timestamp);
      const dateB = new Date(b.timestamp);
      return dateB - dateA;
    });
  }, [filteredLogData]);

  // Filter logs by selected level
  const filterLogsByLevel = (logs, level) => {
    if (!logs) return '';
    // If INFO is selected, return full unfiltered logs
    if (level === 'INFO') return logs;
    // For other levels, filter only matching logs
    return logs.split('\n').filter(line => {
      if (level === 'ERROR') return /error|failed/i.test(line);
      if (level === 'WARN') return /warn|warning/i.test(line);
      if (level === 'DEBUG') return /debug/i.test(line);
      return true;
    }).join('\n');
  };

  // Format MAC address
  const formatMacAddress = (macAddress) => {
    return macAddress ? macAddress.replace(/:/g, '') : '';
  };

  // Handle opening log file
  const handleOpenFile = async (macAddress, level = 'INFO', unitId) => {
    setIsLoading(true);
    setSelectedLogLevel(level);
    const formattedMacAddress = formatMacAddress(macAddress);
    setLogMacAddress(formattedMacAddress);
    const payload = { filename: `${formattedMacAddress}_debug.txt` };

    try {
      const response = await axios.post('https://mdhelperservice.onrender.com/open-file', payload);
      if (response.status === 200) {
        const content = response.data.content || response.data.msg;
        setLogData(content);

        // Set the selected log level for this unit
        setSelectedLogLevels(prev => ({ ...prev, [unitId]: level }));

        if (level === 'INFO') {
          setFilteredLogData(content);
        } else {
          const filtered = content
            .split('\n')
            .filter(line => {
              if (level === 'ERROR') return /error|failed/i.test(line);
              if (level === 'WARN') return /warn|warning/i.test(line);
              if (level === 'DEBUG') return /debug/i.test(line);
              return true;
            })
            .join('\n');
          setFilteredLogData(filtered);
        }
        setIsModalOpenLog(true);
      } else {
        setLogData(response.data.msg);
        setFilteredLogData(response.data.msg);
        setIsModalOpenLog(true);
      }
    } catch (error) {
      setLogData('Error opening file');
      setFilteredLogData('Error opening file');
      setIsModalOpenLog(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete confirmation handlers
  const handleDelete = (row) => {
    setCurrentRow(row);
    setIsModalOpen(true);
  };

  const closeDeleteModal = () => setIsModalOpen(false);

  const confirmDelete = () => {
    if (currentRow) onDelete(currentRow.unitId);
    setIsModalOpen(false);
  };

  // Column definitions
  const displayNames = {
    unitId: "Device ID",
    macAddress: "Mac Address",
    hardwareDetails: "Hardware Details",
    category: "Category",
    description: "Name",
    venue: "Venue",
    version: "Version",
    entryway: "Entryway",
    company: "Company",
    heartbeat: "Status",
    actions: 'Actions',
    error_description: 'Error Log',
    debug_log: 'Debug Log'
  };

  // Log table columns for react-data-table-component with enhanced styling
  const [logTableColumns, setLogTableColumns] = useState([
    {
      name: 'Time Stamp',
      selector: row => row.timestamp,
      sortable: true,
      searchTerm: '',
      width: '220px',
      cell: row => (
        <div className="flex items-center gap-2 py-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg" style={{ backgroundColor: '#F9FAFB' }}>
            <svg className="w-4 h-4" style={{ color: '#667085' }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
            </svg>
          </div>
          <span className="font-medium" style={{ color: '#344054', fontSize: '13px' }}>{row.timestamp}</span>
        </div>
      ),
    },
    {
      name: 'Level',
      selector: row => row.level,
      sortable: true,
      width: '150px',
      cell: row => {
        const levelInfo = logLevels.find(l => l.value === row.level) || logLevels[2];
        return (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full font-semibold" 
               style={{ 
                 backgroundColor: `${levelInfo.color}15`,
                 color: levelInfo.color,
                 fontSize: '12px'
               }}>
            {React.cloneElement(levelInfo.icon, { className: 'text-base' })}
            <span>{row.level}</span>
          </div>
        );
      },
      searchTerm: '',
    },
    {
      name: 'Event',
      selector: row => row.event,
      sortable: true,
      searchTerm: '',
      grow: 2,
      cell: row =>
        row.event && row.event.trim() !== '' ? (
          <div className="py-2">
            {row.event.split('\n').map((ev, idx) => (
              <div key={idx} className="flex items-start mb-1">
                <span className="font-mono text-sm leading-relaxed" style={{ color: '#475467' }}>
                  {ev}
                </span>
              </div>
            ))}
          </div>
        ) : null,
    },
  ]);

  // Ensure headings are unique and in the correct order
  const columns = headings.map(heading => ({
    name: displayNames[heading],
    selector: row => row[heading],
    sortable: !['debug_log', 'actions', 'Status', 'Actions'].includes(heading),
    sortFunction: !['debug_log', 'actions', 'Status', 'Actions'].includes(heading) ? 
      (rowA, rowB, sortDirection) => sortText(rowA, rowB, row => row[heading], sortDirection) : undefined,
    wrap: true,
    cell: (row) => {
      if (heading === 'actions') {
        return (
          <div className="flex flex-row items-center gap-2 text-gray-600">
            {(storedRole === "System Admin" || storedRole === "Company Admin") && (
              <>
                <img onClick={() => handleDelete(row)} src={trash} alt="Delete" className="w-5 h-5 cursor-pointer" />
                <button onClick={() => handleDelete(row)} className="text-red-500">Delete</button>
              </>
            )}
          </div>
        );
      }

      if (heading === 'debug_log') {
        return (
          <button
            onClick={() => handleOpenFile(row.macAddress, 'INFO', row.unitId)}
            className="transition-all duration-200 hover:shadow-lg transform hover:scale-105"
            style={{ 
              backgroundColor: '#010808', 
              color: '#FFFFFF',
              padding: '6px 12px',
              borderRadius: '6px',
              fontWeight: '500',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '6px',
              whiteSpace: 'nowrap',
              border: 'none',
              cursor: 'pointer',
              fontSize: '13px'
            }}
            title="Click to view debug logs"
          >
            <MdBugReport style={{ fontSize: '16px' }} />
            <span>Logs</span>
          </button>
        );
      }

      if (heading === 'heartbeat') {
        const color = row.heartbeat === 'Active' ? '#027A48' : '#B42318';
        const backgroundColor = row.category === 'Control' ? (row.heartbeat === 'Active' ? '#ECFDF3' : '#FEF3F2') : 'transparent';

        return (
          <button className="flex flex-row items-center gap-2 px-2 py-1" style={{ backgroundColor }}>
            <span style={{ color, width: '50px', justifyContent: 'center', alignItems: 'center' }}>
              {row.category === 'Control' ? row.heartbeat : ''}
            </span>
          </button>
        );
      }

      if (heading === 'Status') {
        return (
          <div className="flex flex-row items-center gap-2 text-gray-600">
            {(storedRole === "System Admin" || storedRole === "Company Admin") && (
              <button
                className='py-2.5 px-1'
                onClick={() => {
                  const button = document.getElementById(`status-btn-${row.unitId}`);
                  if (button) button.textContent = 'Checking';
                  onStatusCheck(row);
                  setTimeout(() => {
                    if (button) button.textContent = 'Check Status';
                  }, 3000);
                }}
                id={`status-btn-${row.unitId}`}
                style={{ backgroundColor: '#010808', color: '#FFFFFF' }}
              >
                Check Status
              </button>
            )}
          </div>
        );
      }

      if (heading === 'error_description') {
        const handleErrorClick = () => {
          if (row.error_description.toLowerCase() !== 'no_error' && 
              (storedRole === "System Admin" || storedRole === "Company Admin")) {
            setCurrentErrorDescription(row.error_description);
            setCodeCurrentError(row.errorcode);
            setIsErrorModalOpen(true);
          }
        };

        if (storedRole === "System Admin" || storedRole === "Company Admin") {
          return (
            <div
              className="flex flex-row items-center gap-2 text-gray-600 cursor-pointer"
              style={{
                backgroundColor: row.error_description.toLowerCase() === 'no_error' ? '#027A48' : '#027A48',
                color: '#FFFFFF',
                borderRadius: '0px'
              }}
              onClick={handleErrorClick}
            >
              <button className='py-2 px-2' style={{ width: '70px', justifyContent: 'center', alignItems: 'center' }}>
                {row.error_description.toLowerCase() === 'no_error' ? 'No Error' : 'No Error'}
              </button>
            </div>
          );
        }
        return <div className="flex flex-row items-center gap-2 text-gray-600">{""}</div>;
      }

      if (heading === 'Actions') {
        return (
          <div className="flex flex-row items-center gap-2 text-gray-600">
            {(storedRole === "System Admin" || storedRole === "Company Admin") && (
              <>
                <img
                  onClick={() => onEditDevice(row)}
                  src={edit}
                  alt="Edit"
                  className="w-5 h-5 cursor-pointer"
                />
                <button onClick={() => onEditDevice(row)} className="hover:underline text-gray-600">
                  Edit
                </button>
              </>
            )}
          </div>
        );
      }

      if (heading === 'Actionss') {
        return (
          <div className="flex flex-row items-center gap-2 text-gray-600">
            {(storedRole === "System Admin" || storedRole === "Company Admin") && (
              <>
                <img
                  onClick={() => onSettingsClick(row)}
                  src={edit} alt="Edit"
                  className="w-5 h-5"
                  style={{ cursor: 'pointer' }}
                />
                <button
                  onClick={() => onSettingsClick(row)}
                  className="hover:underline text-gray-600"
                >
                  Settings
                </button>
              </>
            )}
          </div>
        );
      }

      return row[heading];
    }
  }));

  const customStyles = {
    rows: {
      style: {
        minHeight: '62px',
      }
    },

    headCells: {
      style: {
        backgroundColor: '#F9FAFB',
        paddingLeft: '10px',
        paddingRight: '8px',
      },
    },
    cells: {
      style: {
        paddingLeft: '10px',
        paddingRight: '10px',
      },
    },
  };

  const filterAndSetLogData = (logText, columns) => {
    let filtered = logText
      .split('\n')
      .filter(line => {
        return columns.every(col => {
          if (!col.searchTerm) return true;
          if (col.name === 'Time Stamp') return line.toLowerCase().includes(col.searchTerm.toLowerCase());
          if (col.name === 'Level') return line.toLowerCase().includes(col.searchTerm.toLowerCase());
          if (col.name === 'Event') return line.toLowerCase().includes(col.searchTerm.toLowerCase());
          return true;
        });
      })
      .join('\n');
    setFilteredLogData(filtered);
  };

  return (
    <div className='table-container'>
      <DataTable
        columns={columns}
        data={rows}
        responsive
        pagination
        paginationPerPage={10}
        paginationRowsPerPageOptions={[10, 20, 50, 100]}
        persistTableHead
        highlightOnHover
        customStyles={customStyles}
        sortIcon={customSortIcon}
        noHeader
        className="data-table"
        progressPending={isLoading}
        progressComponent={
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        }
      />

      {/* Error Details Modal */}
      <Modal isOpen={isErrorModalOpen} onClose={() => setIsErrorModalOpen(false)}>
        <div className='flex flex-row justify-between items-center'>
          <h2 className='text-gray-900'>Error Details</h2>
          <img 
            onClick={() => setIsErrorModalOpen(false)}
            src={close}
            alt="Close"
            className="cursor-pointer" 
          />
        </div>
        <div className="mt-4">
          <p className='text-gray-900'>Error Code: {codeError}</p>
          <p className='text-gray-900'>Error Description: {currentErrorDescription}</p>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isModalOpen} onClose={closeDeleteModal}>
        <div className='flex flex-row justify-between items-center'>
          <h2 className='text-gray-900'>Delete device</h2>
          <img onClick={closeDeleteModal} src={close} alt="User" className="cursor-pointer" />
        </div>
        <div>
          <p className='text-gray-500'>Are you sure you want to delete this device?</p>
        </div>
        <div className="mt-8 flex justify-spacebetween gap-4">
          <button
            style={{ backgroundColor: '#FFFFF' }}
            onClick={closeDeleteModal}
            className="px-12 md:px-20 py-2 text-gray-600 border rounded"
          >
            Cancel
          </button>
          <button
            style={{ backgroundColor: '#F05D38' }}
            onClick={confirmDelete}
            className="px-12 md:px-20 py-2 text-white bg-red-500 rounded"
          >
            Delete
          </button>
        </div>
      </Modal>

      {/* Log Viewer Modal - Enhanced Beautiful UI */}
      <Modals isOpen={isModalOpenLog} onClose={() => setIsModalOpenLog(false)} className="max-w-4xl">
        <div className="flex flex-col gap-4 md:gap-6 p-4 md:p-6 h-full" style={{ backgroundColor: '#FAFAFA' }}>
          {/* Modern Header with Gradient Background */}
          <div className="relative rounded-xl p-4 md:p-6 shadow-lg log-viewer-header" style={{ 
            background: '#d1d5db',
            borderBottom: '4px solid #D1D5DB'
          }}>
            <div className="flex flex-row justify-between items-center gap-4 w-full">
              <div className="flex items-center gap-3 md:gap-4 flex-shrink-0">
                <div className="p-2 md:p-3 rounded-lg flex-shrink-0" style={{ backgroundColor: '#E5E7EB' }}>
                  <MdBugReport className="text-2xl md:text-3xl" style={{ color: '#374151' }} />
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl md:text-2xl font-bold whitespace-nowrap" style={{ color: '#111827' }}>Debug Log Viewer</h2>
                  <p className="text-xs md:text-sm mt-1 break-words" style={{ color: '#6B7280' }}>
                    Device MAC: {logMacAddress || 'N/A'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsModalOpenLog(false)}
                className="p-2 rounded-lg transition-all duration-200 hover:scale-110 flex-shrink-0"
                style={{ backgroundColor: '#E5E7EB', color: '#374151' }}
              >
                <MdClose className="text-xl md:text-2xl" />
              </button>
            </div>
          </div>

          {/* Log Level Dropdown Section */}
          <div className="flex items-center justify-start gap-3">
            <label className="text-sm font-medium" style={{ color: '#667085' }}>Filter by Level:</label>
            <div className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg" style={{ backgroundColor: '#E5E7EB' }}>
              <LogLevelDropdown
                value={selectedLogLevel}
                onChange={val => {
                  setSelectedLogLevel(val);
                  setFilteredLogData(filterLogsByLevel(logData, val));
                }}
                options={logLevels}
              />
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
            {logLevels.map((level, idx) => {
              const count = sortedLogData.filter(log => log.level === level.value).length;
              return (
                <div 
                  key={level.value}
                  className="log-stats-card p-3 md:p-4 rounded-xl shadow-md transform transition-all duration-200 hover:scale-105 hover:shadow-lg cursor-pointer"
                  style={{ 
                    backgroundColor: '#FFFFFF',
                    border: selectedLogLevel === level.value ? `2px solid ${level.color}` : '2px solid transparent'
                  }}
                  onClick={() => {
                    setSelectedLogLevel(level.value);
                    setFilteredLogData(filterLogsByLevel(logData, level.value));
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className="p-1.5 md:p-2 rounded-lg flex-shrink-0" style={{ backgroundColor: `${level.color}15` }}>
                        {React.cloneElement(level.icon, { 
                          className: 'text-lg md:text-xl',
                          style: { color: level.color }
                        })}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium truncate" style={{ color: '#667085' }}>{level.label}</p>
                        <p className="text-xl md:text-2xl font-bold" style={{ color: level.color }}>{count}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Enhanced Search Section */}
          <div className="log-search-section bg-white rounded-xl shadow-md p-4 md:p-5">
            <div className="flex items-center gap-2 mb-3 md:mb-4">
              <MdSearch className="text-lg md:text-xl" style={{ color: '#667085' }} />
              <h3 className="text-base md:text-lg font-semibold" style={{ color: '#010808' }}>Search Filters</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              {logTableColumns.filter(column => column.name !== 'Level').map(column => {
                const levelObj = logLevels.find(l => l.value === selectedLogLevel);
                const levelColor = levelObj ? levelObj.color : '#000';
                return (
                  <div key={column.name} className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                      <MdSearch className="text-lg transition-all duration-200 group-focus-within:scale-110" style={{ color: levelColor }} />
                    </div>
                    <input
                      type="text"
                      placeholder={`Search ${column.name}...`}
                      value={column.searchTerm || ''}
                      onChange={async (e) => {
                        const value = e.target.value;
                        const updatedColumns = logTableColumns.map(col =>
                          col.name === column.name ? { ...col, searchTerm: value } : col
                        );
                        setLogTableColumns(updatedColumns);

                        const payload = {
                          filename: logMacAddress ? `${logMacAddress}_debug.txt` : '_debug.txt'
                        };

                        if (!logData || !value) {
                          setIsLoading(true);
                          try {
                            const response = await axios.post('https://mdhelperservice.onrender.com/open-file', payload);
                            if (response.status === 200) {
                              const content = response.data.content || response.data.msg;
                              setLogData(content);
                              filterAndSetLogData(content, updatedColumns);
                            } else {
                              setLogData(response.data.msg);
                              setFilteredLogData(response.data.msg);
                            }
                          } catch (error) {
                            setLogData('Error opening file');
                            setFilteredLogData('Error opening file');
                          } finally {
                            setIsLoading(false);
                          }
                        } else {
                          filterAndSetLogData(logData, updatedColumns);
                        }
                      }}
                      className="pl-12 pr-4 py-3 w-full rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:scale-105"
                      style={{ 
                        border: '2px solid #E5E7EB',
                        color: levelColor,
                        backgroundColor: '#FFFFFF'
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Beautiful Table Container */}
          <div className="log-table-container flex-1 rounded-xl overflow-hidden shadow-xl" style={{ backgroundColor: '#FFFFFF' }}>
            <div className="log-viewer-scroll overflow-auto h-full w-full max-h-[50vh] md:max-h-[55vh] px-2 md:px-0">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                  <div className="relative">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-t-transparent" 
                         style={{ borderColor: '#667085', borderTopColor: 'transparent' }}></div>
                    <MdBugReport className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-2xl" 
                                 style={{ color: '#667085' }} />
                  </div>
                  <p className="text-lg font-medium" style={{ color: '#667085' }}>Loading logs...</p>
                </div>
              ) : sortedLogData.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                  <div className="p-6 rounded-full" style={{ backgroundColor: '#F9FAFB' }}>
                    <MdInfo className="text-5xl" style={{ color: '#667085' }} />
                  </div>
                  <p className="text-lg font-medium" style={{ color: '#667085' }}>No logs found</p>
                  <p className="text-sm" style={{ color: '#98A2B3' }}>Try adjusting your filters or log level</p>
                </div>
              ) : (
                <DataTable
                  columns={logTableColumns}
                  data={sortedLogData}
                  responsive
                  pagination
                  paginationPerPage={10}
                  paginationRowsPerPageOptions={[10, 20, 50, 100]}
                  highlightOnHover
                  customStyles={{
                    cells: {
                      style: {
                        padding: '16px 20px',
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                        fontSize: '14px',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                      },
                    },
                    rows: {
                      style: {
                        minHeight: '60px',
                        borderBottom: '1px solid #F2F4F7',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: '#F9FAFB',
                          transform: 'scale(1.01)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        },
                      },
                    },
                    headCells: {
                      style: {
                        backgroundColor: '#F9FAFB',
                        fontWeight: '700',
                        fontSize: '13px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        padding: '18px 20px',
                        color: '#344054',
                        borderBottom: '2px solid #E4E7EC',
                      },
                    },
                    pagination: {
                      style: {
                        borderTop: '2px solid #E4E7EC',
                        padding: '16px',
                        backgroundColor: '#FAFAFA',
                      },
                    },
                  }}
                  noHeader
                />
              )}
            </div>
          </div>

          {/* Footer Info */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0 px-3 md:px-4 py-3 rounded-lg" style={{ backgroundColor: '#F9FAFB' }}>
            <div className="flex items-center gap-2">
              <MdInfo className="text-base md:text-lg" style={{ color: '#667085' }} />
              <span className="text-xs md:text-sm font-medium" style={{ color: '#667085' }}>
                Total Logs: <strong>{sortedLogData.length}</strong>
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs md:text-sm" style={{ color: '#667085' }}>
              <span>Level:</span>
              <span className="px-2 md:px-3 py-1 rounded-full font-semibold text-xs md:text-sm" 
                    style={{ 
                      backgroundColor: logLevels.find(l => l.value === selectedLogLevel)?.color + '20',
                      color: logLevels.find(l => l.value === selectedLogLevel)?.color 
                    }}>
                {selectedLogLevel}
              </span>
            </div>
          </div>
        </div>
      </Modals>
    </div>
  );
};

export default TableDevice;